import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import assign from 'lodash/assign';
import noop from 'lodash/noop';
import { wrapProtectedActions } from '../../../../actions/auth';
import {
  favoriteCurrentlyPlayingItem,
  unFavoriteCurrentlyPlayingItem,
  favoriteContainerItem,
  unFavoriteContainerItem,
} from '../../../../actions/favorites';
import ActionDrawerHoverContext from '../../actionDrawer/context/ActionDrawerHoverContext';
import isFollowingGuideId from '../../../../utils/isFollowingGuideId';
import iconCss from './favorite-icon.module.scss';

export function FavoriteIconControl({
  isContainerItemFavorite,
  isFollowing,
  guideId,
  children,
  isOptionsController,
  isOption,
  isMobile,
  actions,
}) {
  const [isHovering, setIsHovering] = useState(false);
  // This effect prevents the icon from changing immediately after toggling favorite due to hover.
  // Hover state is reset on next mouseEnter.
  useEffect(() => setIsHovering(false), [isFollowing]);
  const { handleMouseEnter, handleMouseLeave } = useContext(
    ActionDrawerHoverContext,
  );

  function handleFavoriteAction() {
    if (isContainerItemFavorite) {
      if (isFollowing) {
        actions.unFavoriteContainerItem({ guideId });
      } else {
        actions.favoriteContainerItem({ guideId });
      }
      return;
    }

    if (isFollowing) {
      actions.unFavoriteCurrentlyPlayingItem(guideId);
    } else {
      actions.favoriteCurrentlyPlayingItem(guideId);
    }
  }

  const containerProps = {
    'data-testid': `favorite-icon-control-${guideId}`,
    onClick: handleFavoriteAction,
    onMouseEnter: isMobile ? noop : () => setIsHovering(true),
    onMouseLeave: isMobile ? noop : () => setIsHovering(false),
  };

  if (isOptionsController) {
    assign(containerProps, {
      onClick: noop,
      onMouseEnter: handleMouseEnter || noop,
      onMouseLeave: handleMouseLeave || noop,
    });
  }

  if (!isOption) {
    containerProps.className = iconCss.container;
  }

  return <div {...containerProps}>{children({ isFollowing, isHovering })}</div>;
}

FavoriteIconControl.propTypes = {
  // inherited
  guideId: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
  isOptionsController: PropTypes.bool,
  isOption: PropTypes.bool,
  isContainerItemFavorite: PropTypes.bool,

  // mapStateToProps
  isFollowing: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,

  // mapDispatchToProps
  actions: PropTypes.exact({
    favoriteCurrentlyPlayingItem: PropTypes.func.isRequired,
    unFavoriteCurrentlyPlayingItem: PropTypes.func.isRequired,
    favoriteContainerItem: PropTypes.func.isRequired,
    unFavoriteContainerItem: PropTypes.func.isRequired,
  }).isRequired,
};

FavoriteIconControl.defaultProps = {
  isOption: false,
  isOptionsController: false,
};

function mapStateToProps(state, ownProps) {
  const { guideId, isFollowing: initialFollowState } = ownProps;
  const isFollowing = isFollowingGuideId(state, guideId, initialFollowState);
  return {
    isMobile: state.app.isMobile,
    isFollowing,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      wrapProtectedActions({
        favoriteCurrentlyPlayingItem,
        unFavoriteCurrentlyPlayingItem,
        favoriteContainerItem,
        unFavoriteContainerItem,
      }),
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(FavoriteIconControl);
